import {useEffect} from 'react';
import {Zap} from 'lucide-react';
import {useFunnel} from '@tinct/web-funnel';
import {Alert, AlertDescription, AlertTitle, Button, ErrorPlaceholder, JobLoader} from '@tinct/web-components';
import {pluralize, useStaticAssetsUrl} from '@tinct/core-utils';
import {CampaignCreationFunnelState} from '../../campaign-creation-funnel/campaign-creation-funnel';
import {useSampleGenerationJob} from './hooks/use-sample-generation-job';
import {useOpenPaywall} from "@tinct/web-feature-payment";

export const Step5SampleGeneration = () => {
  const {
    state: {campaign},
  } = useFunnel<CampaignCreationFunnelState>();
  const campaignId = campaign.id;

  if (!campaignId) {
    return <div>No campaign ID found.</div>;
  }

  return <Step5SampleGenerationContent campaignId={campaignId} />;
};

const Step5SampleGenerationContent = ({campaignId}: {campaignId: string}) => {
  const {navigateToNextStep} = useFunnel();
  const assetsUrl = useStaticAssetsUrl();
  const {
    subJobs,
    allReady,
    isGenerationFailed,
    hasNotEnoughCredits,
    remainingLabel,
    pageCount,
    retryAll,
    gameModal,
  } = useSampleGenerationJob(campaignId);
  const {openPaywall} = useOpenPaywall();

  useEffect(() => {
    if (hasNotEnoughCredits) {
      openPaywall('not_enough_credits');
    }
  }, [hasNotEnoughCredits, openPaywall]);

  useEffect(() => {
    if (!allReady) return;
    const timeout = setTimeout(() => {
      navigateToNextStep(true);
    }, 1500);
    return () => clearTimeout(timeout);
  }, [allReady, navigateToNextStep]);

  if (isGenerationFailed) {
    return (
      <ErrorPlaceholder
        image={
          <img
            src={`${assetsUrl}/error-analyze.png`}
            alt=""
            className="h-[140px] w-[280px] object-cover"
          />
        }
        title="Sample generation failed"
        description="We couldn't generate sample pages for this campaign. Please try again — if the problem persists, contact support."
        actions={
          <>
            <Button size="sm" onClick={retryAll}>
              Refresh to retry
            </Button>
            <Button size="sm" variant="outline" asChild>
              <a href="mailto:support@tinct.ai">Contact support</a>
            </Button>
          </>
        }
      />
    );
  }

  return (
    <div className="container mx-auto flex w-full flex-col items-center justify-center">
      <div className="flex w-full max-w-xl flex-col gap-8">
        <div className="flex items-end gap-4">
          <div className="flex flex-1 flex-col gap-2">
            <h2 className="font-heading text-3xl font-semibold tracking-tight text-card-foreground">
              Generating first {pageCount} {pluralize(pageCount, 'page')} for campaign preview...
            </h2>
            <p className="text-xs text-muted-foreground">
              {remainingLabel}
            </p>
          </div>
          <div className="flex shrink-0 items-end">
            <img
              src={`${assetsUrl}/run-sampling.png`}
              alt=""
              className="w-[100px]"
            />
          </div>
        </div>

        {/* Quota block freezes the whole step (auto-start and retries no-op),
            so the reason must stay visible after the paywall is dismissed. */}
        {hasNotEnoughCredits && (
          <Alert variant="destructive">
            <Zap className="size-4" />
            <AlertTitle>Generation paused — not enough credits</AlertTitle>
            <AlertDescription className="flex flex-col items-start gap-3">
              <span>
                Your workspace is out of page-generation credits, so sample
                pages can&apos;t be generated. Upgrade to continue.
              </span>
              <Button size="sm" onClick={() => openPaywall('not_enough_credits')}>
                <Zap className="size-4" />
                Upgrade
              </Button>
            </AlertDescription>
          </Alert>
        )}

        <JobLoader subJobs={subJobs} />
      </div>
      {gameModal}
    </div>
  );
};
